import React from 'react';
import Header from "./header";
import Footer from "./footer";

function Policy() {
    return (
        <div className="min-h-screen flex flex-col bg-bg font-sans">
            <Header />

            <div className="flex-1 container pt-24 pb-16 max-w-3xl mx-auto px-4">
                <h1 className="text-3xl font-extrabold tracking-tight text-text mb-2">Peticle — Privacy Policy</h1>
                <p className="text-muted mb-8">Effective Date: 1 June 2025</p>

                <p className="mb-6">
                    Welcome to Peticle, a pet health insights app created by Unit 326. This policy explains
                    how we collect, use, and protect your data when you use our app. We are committed to safeguarding
                    your privacy and ensuring a secure user experience.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">1. Interpretation and Definitions</h2>

                <h3 className="text-lg font-semibold text-brand mb-2">Definitions</h3>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-muted">
                    <li><strong>Account</strong> — a unique account created for You to access our Service.</li>
                    <li><strong>Application</strong> — refers to Peticle, the software program provided by the Company.</li>
                    <li><strong>Company</strong> (referred to as "We", "Us" or "Our") — refers to Unit 326.</li>
                    <li><strong>Device</strong> — any device that can access the Service such as a smartphone or tablet.</li>
                    <li><strong>Personal Data</strong> — any information that relates to an identified or identifiable individual.</li>
                    <li><strong>Pet Data</strong> — photos, health observations, and other information you provide about your pet(s).</li>
                    <li><strong>Service</strong> — refers to the Peticle application.</li>
                    <li><strong>You</strong> — the individual accessing or using the Service.</li>
                </ul>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">2. Data We Collect</h2>

                <h3 className="text-lg font-semibold text-brand mb-2">Personal Data</h3>
                <p className="mb-4">While using our Service, we may ask you to provide:</p>
                <ul className="list-disc pl-6 space-y-1 mb-6 text-muted">
                    <li>Email address</li>
                    <li>First and last name</li>
                    <li>Account credentials</li>
                    <li>Usage data</li>
                </ul>

                <h3 className="text-lg font-semibold text-brand mb-2">Pet & Photo Data</h3>
                <p className="mb-4 text-muted">
                    Peticle collects photos you submit for analysis. These photos are used solely to generate
                    health observations for your pet and to maintain your pet's health history within the app.
                    Photos are processed securely and are not shared with third parties for advertising purposes.
                </p>

                <h3 className="text-lg font-semibold text-brand mb-2">Usage Data</h3>
                <p className="mb-6 text-muted">
                    Usage data is collected automatically and may include your device's IP address, browser type,
                    pages visited, time and date of visits, time spent on pages, and other diagnostic data.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">3. How We Use Your Data</h2>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-muted">
                    <li><strong>To provide and maintain our Service</strong>, including  health scan analysis.</li>
                    <li><strong>To manage Your Account</strong> and provide access to features.</li>
                    <li><strong>To improve the Service</strong> through usage analysis and feature development.</li>
                    <li><strong>To contact You</strong> with important updates, security notices, or support responses.</li>
                    <li><strong>To process your subscription</strong> and manage billing.</li>
                </ul>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">4. Camera & Photo Library Access</h2>
                <p className="mb-4 text-muted">
                    Peticle uses your camera and photo library to capture or select images of your pet for
                    health analysis (e.g. health scans, fecal analysis, breed identification).
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-muted">
                    <li>Camera access is used only when you actively initiate a scan within the app.</li>
                    <li>Photo library access is used only to let you select an existing photo.</li>
                    <li>We do not access your camera or photo library in the background.</li>
                    <li>Photos are transmitted securely over HTTPS and processed by our  analysis service.</li>
                </ul>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">5. Data Sharing</h2>
                <p className="mb-4 text-muted">We may share your information:</p>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-muted">
                    <li><strong>With Service Providers</strong> — to facilitate the Service (e.g. cloud hosting, processing, analytics).</li>
                    <li><strong>For business transfers</strong> — in connection with a merger or acquisition.</li>
                    <li><strong>With your consent</strong> — for any other purpose you have agreed to.</li>
                </ul>
                <p className="mb-6 text-muted">We never sell your personal data or pet photos to third parties.</p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">6. Data Retention</h2>
                <p className="mb-6 text-muted">
                    We retain your Personal Data only as long as necessary for the purposes set out in this policy,
                    to comply with legal obligations, resolve disputes, and enforce our agreements.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">7. Deleting Your Data</h2>
                <p className="mb-4 text-muted">
                    You have the right to delete or request deletion of your Personal Data. You can:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-muted">
                    <li>Delete individual pet profiles or scan history within the app.</li>
                    <li>Request full account and data deletion via our <a href="/data-settings" className="text-brand underline">Data Settings</a> page.</li>
                    <li>Email us at <a href="mailto:hello@unit326.com" className="text-brand underline">hello@unit326.com</a>.</li>
                </ul>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">8. Data Security</h2>
                <p className="mb-6 text-muted">
                    We employ industry-standard security measures including encryption in transit (HTTPS) and at rest.
                    Access to your data is restricted to authorised personnel only.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">9. Third-Party Sign-In</h2>
                <p className="mb-6 text-muted">
                    Peticle allows you to sign in using your Google or Apple account. Please refer to their respective
                    privacy policies for information on how they handle your data.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">10. Children's Privacy</h2>
                <p className="mb-6 text-muted">
                    Peticle is not intended for individuals under the age of 13. We do not knowingly collect personal
                    information from children. If you believe a child has provided us with their information, please
                    contact us to have it removed.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">11. Disclaimer</h2>
                <p className="mb-6 text-muted">
                    Peticle provides informational insights only. It is <strong>not</strong> a veterinary diagnostic tool
                    and does not replace professional veterinary advice, examination, or treatment. Always consult a
                    qualified vet for your pet's health concerns.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">12. Changes to This Policy</h2>
                <p className="mb-6 text-muted">
                    We reserve the right to modify this policy at any time. Changes will be effective upon posting in
                    the app. It is your responsibility to review this policy periodically for updates.
                </p>

                <h2 className="text-xl font-bold text-text mt-8 mb-3">13. Contact Us</h2>
                <p className="mb-6 text-muted">
                    If you have any questions about this Privacy Policy, please contact us at{' '}
                    <a href="mailto:hello@unit326.com" className="text-brand underline">hello@unit326.com</a>.
                </p>

                <p className="text-muted text-sm mt-10">Thank you for choosing Peticle.</p>
            </div>

            <Footer />
        </div>
    );
}

export default Policy;
